/**
 * The brand's design tokens - the one place a colour, font or radius is decided.
 *
 * Every consumer reads from here: the MUI v9 theme (`muiTheme.ts`), the MUI v4 theme
 * (`legacyMuiTheme.ts`), Tailwind (`tailwind.config.ts`, which maps `brand-*`, `accent-*`, `gray-*` and
 * `indigo-*` onto these scales) and `src/utils/colors.ts`. A rebrand changes values in this file and
 * nothing else; see "Theme and brand tokens" in AGENTS.md.
 *
 * Plain data with no imports, so the Tailwind config can load it outside the app bundle.
 *
 * Seeded with what the app drew before the theme existed: `primary`/`secondary` are MUI v4's defaults
 * (the app bar, and every `color="primary"`), and `accent`/`neutral` are Tailwind's own default
 * `indigo`/`gray` scales, so mounting the theme moved no pixels on Tailwind or v4 screens.
 */
export const tokens = {
  primary: {
    light: '#7986cb',
    main: '#3f51b5',
    dark: '#303f9f',
    contrastText: '#ffffff'
  },
  secondary: {
    light: '#ff4081',
    main: '#f50057',
    dark: '#c51162',
    contrastText: '#ffffff'
  },

  // The second accent the newer widgets use - Tailwind `indigo-*`, and the indigo hex literals the
  // charts carried. MUI reads only hex, so the scale is Tailwind-only.
  accent: {
    50: 'oklch(96.2% 0.018 272.314)',
    100: 'oklch(93% 0.034 272.788)',
    200: 'oklch(87% 0.065 274.039)',
    300: 'oklch(78.5% 0.115 274.713)',
    400: 'oklch(67.3% 0.182 276.935)',
    500: 'oklch(58.5% 0.233 277.117)',
    600: 'oklch(51.1% 0.262 276.966)',
    700: 'oklch(45.7% 0.24 277.023)',
    800: 'oklch(39.8% 0.195 277.366)',
    900: 'oklch(35.9% 0.144 278.697)',
    950: 'oklch(25.7% 0.09 281.288)'
  },

  // Text, borders and surfaces - Tailwind `gray-*`, the most used colour family in the app.
  neutral: {
    50: 'oklch(98.5% 0.002 247.839)',
    100: 'oklch(96.7% 0.003 264.542)',
    200: 'oklch(92.8% 0.006 264.531)',
    300: 'oklch(87.2% 0.01 258.338)',
    400: 'oklch(70.7% 0.022 261.325)',
    500: 'oklch(55.1% 0.027 264.364)',
    600: 'oklch(44.6% 0.03 256.802)',
    700: 'oklch(37.3% 0.034 259.733)',
    800: 'oklch(27.8% 0.033 256.848)',
    900: 'oklch(21% 0.034 264.665)',
    950: 'oklch(13% 0.028 261.692)'
  },

  // Meaning, not brand: `colors.ts` reads these. The MUI themes leave their status palettes at MUI's
  // defaults, which is what both generations drew before the theme existed.
  status: {
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3'
  },

  // The sidebar drawer and the grey panels that match it (`colors.defaultGrey`).
  sidebar: '#eceff1',

  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  borderRadius: 4
} as const;
